"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFieldArray = exports.FieldArray = void 0;
const react_1 = require("react");
/**
 * Manages fields sharing a common prefix with an index number,
 * e.g. data.myArray.0, data.myArray.1
 */
class FieldArray {
    /**
     * Creates a new field array
     * @param fieldPrefix The prefix used for all fields in this array
     * @param onUpdate Callback to use after updates are made
     */
    constructor(fieldPrefix, onUpdate) {
        /**
         * Current field index names, in order
         *
         * @example
         * ["myArray.3", "myArray.1"]
         *
         * Note: fields retain their old names (including index numbers) until they
         * are remapped, so the order in this array is the true order, not the names
         *
         * For example, ["myArray.3", "myArray.1"] means the field named "myArray.3"
         * is first and the field named "myArray.1" is second, and they will be
         * remapped to "myArray.0" and "myArray.1" respectively with
         * {@link FieldArray.remapArrayIndices}
         */
        this.fieldIndexNames = [];
        /** Starting field index names in order (for reset) */
        this.startingFieldIndexNames = [];
        /**
         * The next index to use when a new field is added
         *
         * Note: this may _not_ be the current size of fieldNames,
         * since fields can be removed
         */
        this.nextFieldIndexNumber = 0;
        this.fieldPrefix = fieldPrefix;
        this.onUpdate = onUpdate;
    }
    //////////////////////////////////////////////////////////////////////////////
    // managing the array
    //////////////////////////////////////////////////////////////////////////////
    /** Returns the current index names in the array, in order */
    getFieldIndexNames() {
        return this.fieldIndexNames;
    }
    /** Returns how many indices are being tracked */
    getLength() {
        return this.fieldIndexNames.length;
    }
    /** Adds a new index to the array and returns the generated field index name */
    addFieldIndex() {
        const newFieldIndexName = `${this.fieldPrefix}.${this.nextFieldIndexNumber}`;
        this.fieldIndexNames = [...this.fieldIndexNames, newFieldIndexName];
        this.nextFieldIndexNumber += 1;
        this.onUpdate();
        return newFieldIndexName;
    }
    /** Removes the matching field from the array */
    removeFieldIndex(fieldIndexName) {
        this.fieldIndexNames = this.fieldIndexNames.filter((fn) => fn !== fieldIndexName);
        this.onUpdate();
    }
    /** Moves a field to a new position/index in the array */
    moveFieldIndex(fieldIndexName, toPosition) {
        const currentIndex = this.fieldIndexNames.indexOf(fieldIndexName);
        if (toPosition < 0 || toPosition > this.fieldIndexNames.length - 1) {
            throw new Error(`Field "${fieldIndexName}" would be out of bounds if moved!`);
        }
        // reposition
        this.fieldIndexNames.splice(currentIndex, 1);
        this.fieldIndexNames.splice(toPosition, 0, fieldIndexName);
        this.onUpdate();
    }
    /** Moves a field in the array by a certain distance, e.g. -1 */
    moveFieldIndexBy(fieldIndexName, delta) {
        const newPosition = this.fieldIndexNames.indexOf(fieldIndexName) + delta;
        this.moveFieldIndex(fieldIndexName, newPosition);
    }
    /** Resets the array to its starting state */
    reset() {
        this.fieldIndexNames = this.startingFieldIndexNames;
        this.nextFieldIndexNumber = this.startingFieldIndexNames.length;
        this.onUpdate();
    }
    //////////////////////////////////////////////////////////////////////////////
    // form methods/hooks
    //////////////////////////////////////////////////////////////////////////////
    /**
     * Initializes the array with starting field names
     *
     * Note: this assumes initial indices are correctly ordered,
     * e.g. myArray.0, myArray.1, myArray.2, and that the next index is count(current indices)
     *
     * This will always be true if an array has just been flattened, but custom/weird data
     * sources might need to do some pre-processing first
     */
    initializeFieldArray(allFieldNames) {
        const numArrayIndices = this._countNumberOfArrayIndices(allFieldNames);
        const startingFieldIndexNames = this._generateFieldIndexNames(numArrayIndices);
        this.fieldIndexNames = startingFieldIndexNames;
        this.startingFieldIndexNames = startingFieldIndexNames;
        this.nextFieldIndexNumber = startingFieldIndexNames.length;
    }
    /** Remaps all of this array's fields to new indices, after potential adds/deletes/moves */
    remapArrayIndices(data) {
        return Object.fromEntries(Object.entries(data).map(([fieldName, value]) => [this._remapFieldName(fieldName), value]));
    }
    //////////////////////////////////////////////////////////////////////////////
    // helper methods
    //////////////////////////////////////////////////////////////////////////////
    /**
     * Counts the number of unique array indices found in the provided field names
     *
     * Note: The array entry "data.myArray.0" could have multiple fields if it is
     * an object e.g., "data.myArray.0.prop1", "data.myArray.0.prop2"
     */
    _countNumberOfArrayIndices(fieldNames) {
        const fieldsInArray = fieldNames.filter((fieldName) => fieldName.startsWith(`${this.fieldPrefix}.`));
        return new Set(fieldsInArray.map((name) => name.substring(this.fieldPrefix.length + 1).split(".")[0])).size;
    }
    /**
     * Generate array field names
     *
     * @example
     * // with this.arrayName "myArray"
     * _generateArrayFieldNames(2) => ["myArray.0", "myArray.1"]
     */
    _generateFieldIndexNames(numIndices) {
        return Array.from({ length: numIndices }, (_, i) => `${this.fieldPrefix}.${i}`);
    }
    /**
     * Remaps a field name to its new index, after potential adds/deletes/moves
     *
     * Ignores (no-op) on fields not part this array
     *
     * @example
     * // with this.arrayName = "myArray"
     * // with this.fieldNames = ["myArray.3", "myArray.1"]
     * _remapFieldName("myArray.3") => "myArray.0"
     * _remapFieldName("myArray.1") => "myArray.1"
     * _remapFieldName("otherField") => "otherField"
     */
    _remapFieldName(fieldName) {
        // not part of this array
        if (!fieldName.startsWith(`${this.fieldPrefix}.`)) {
            return fieldName;
        }
        // nested key like data.array.0.a
        if (fieldName.substring(this.fieldPrefix.length + 1).indexOf(".") !== -1) {
            for (const [index, fieldIndexName] of this.fieldIndexNames.entries()) {
                if (fieldName.startsWith(`${fieldIndexName}.`)) {
                    return fieldName.replace(`${fieldIndexName}.`, `${this.fieldPrefix}.${index}.`);
                }
            }
        }
        // plain key like data.array.0
        return `${this.fieldPrefix}.${this.fieldIndexNames.indexOf(fieldName)}`;
    }
}
exports.FieldArray = FieldArray;
/**
 * Creates a new {@link FieldArray} instance
 * @param fieldPrefix The name of the array, i.e. the prefix of each field
 */
const useFieldArray = (fieldPrefix) => {
    const array = (0, react_1.useRef)(undefined);
    const [, updateState] = (0, react_1.useState)({});
    // create once
    if (array.current === undefined) {
        array.current = new FieldArray(fieldPrefix, () => updateState({}));
    }
    return array.current;
};
exports.useFieldArray = useFieldArray;
